import {
  BLOCKS,
  type Block,
  INLINES,
  type Inline,
} from '@contentful/rich-text-types';
import Link from '@mui/material/Link';
import type { TypographyProps } from '@mui/material/Typography';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import type { useStyles } from './RichText.styles';
import type {
  RichTextBlockKey,
  RichTextBlockPropsMap,
  RichTextTypographyBlockKey,
} from './types';
import { resolveTypographyBlockProps } from './utils';

type RichTextClasses = ReturnType<typeof useStyles>['classes'];

interface CreateRenderNodeOptions {
  classes: RichTextClasses;
  cx: (...classes: Array<string | false | undefined>) => string;
  variants?: Partial<
    Record<RichTextTypographyBlockKey, TypographyProps['variant']>
  >;
  blockProps?: RichTextBlockPropsMap;
  blockClasses?: Partial<Record<RichTextBlockKey, string>>;
}

function createTypographyRenderer(
  key: RichTextTypographyBlockKey,
  options: CreateRenderNodeOptions,
  extraClassName?: string
) {
  return (_node: Block | Inline, children: ReactNode) => (
    <Typography
      {...resolveTypographyBlockProps(key, options.cx, {
        variants: options.variants,
        blockProps: options.blockProps,
        blockClasses: options.blockClasses,
        extraProps: extraClassName ? { className: extraClassName } : undefined,
      })}
    >
      {children}
    </Typography>
  );
}

export function createRenderNodeMap(options: CreateRenderNodeOptions) {
  const { classes, cx, blockProps, blockClasses } = options;

  return {
    [INLINES.HYPERLINK]: (node: Block | Inline, children: ReactNode) => {
      if (node.nodeType !== INLINES.HYPERLINK) {
        return children;
      }

      const href = node.data.uri;
      const isExternal = /^https?:\/\//.test(href);

      return (
        <Link
          href={href}
          underline='always'
          {...(isExternal ? { rel: 'noreferrer', target: '_blank' } : {})}
          {...blockProps?.link}
          className={cx(
            classes.link,
            blockClasses?.link,
            blockProps?.link?.className
          )}
        >
          {children}
        </Link>
      );
    },
    [BLOCKS.HEADING_1]: createTypographyRenderer('h1', options),
    [BLOCKS.HEADING_2]: createTypographyRenderer('h2', options),
    [BLOCKS.HEADING_3]: createTypographyRenderer('h3', options),
    [BLOCKS.HEADING_4]: createTypographyRenderer('h4', options),
    [BLOCKS.HEADING_5]: createTypographyRenderer('h5', options),
    [BLOCKS.PARAGRAPH]: createTypographyRenderer('p', options),
  };
}
